import { Modal, ModalBackButton } from '@/components/Modal'
import { contractsActions, contractsSelectors, contractsThunks, userSelectors } from '@/entity'
import { handbookSelectors } from '@/entity/handbook'
import { getCurrentLanguage } from '@/i18next'
import { Button as FormButton, Button, Checkbox, Title } from '@/ui'
import { Button as ButtonMui } from '@mui/material'
import { Edit, Pencil, Trash, Delete, InfoGreen, RefreshIcon, InfoMedium } from '@app/icons'
import { AdditionalContractInformation } from './AdditionalContractInformation'
import { SignStatus } from '@features/contracts/badges'
import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styles from './AdditionalContractStyles.module.scss'
import { request } from '@/shared/api/request'
import cn from 'classnames'
import { LoadingBlocker } from '@/components/Loader'
import { message } from 'antd'
import { downloadFileByUid } from '@/shared/api/rest/pension/downloadFileByUid'
import { EmploymentContractFile } from '@/entity/contracts/ui/form/employmentСontractFile/employmentСontractFile'
import { ViewConfirmDocument } from '@/components/ViewDocuments/ViewConfirmDocument'
import TableSign from '@features/contracts/actions/view/components/TableSign/TableSign'
import { Alert } from '@/components/Alert/Alert'
import { Icon } from '@/components/Icon'

export const ViewAdditionalAgreementModal = ({
	onRevert,
	onRevision,
	onClose,
	onToggleAction,
	onSign,
	filters = {},
	page,
	pageSize,
	setFilters,
	fetchData,
}) => {
	const {
		t,
		i18n: { language },
	} = useTranslation()
	const dispatch = useDispatch()
	const lang = getCurrentLanguage()
	const {
		dcontractStatus,
		dcontractState,
		dpositionCode,
		econtractFound,
		externalId,
		isLocalContract,
		contractNumber,
		registerDate,
		fromFile,
	} = useSelector(contractsSelectors.contract)
	const additionalContract = useSelector(contractsSelectors.additionalContract)
	const positionsByKnz = useSelector(handbookSelectors.positionsByKnz)
	const activeRole = useSelector(userSelectors.activeRole)
	const esutdRegErrors = useSelector((state) => state.handbook.esutdRegErrors)

	const [adContractInfo, setAdContractInfo] = useState(null)
	const [eAdContractToSend, setEAdContractToSend] = useState(null)
	const [mode, setMode] = useState(null)
	const [disableDownload, setDisableDownload] = useState(false)
	const [showFile, setShowFile] = useState(false)
	const [disableButtonNkz, setDisableButtonNkz] = useState(false)
	const [isGuardian, setIsGuardian] = useState(false)
	const [statusKdp, setStatusKdp] = useState('')

	const additionalContractState = additionalContract?.dcontractState?.code
	const status = dcontractStatus?.code
	const state = dcontractState?.code
	const isEmployer = activeRole === 'ROLE_EMPLOYER'
	const isEmployee = activeRole === 'ROLE_EMPLOYEE'
	const errorData = esutdRegErrors.find(
		(item) => item.additionalContractState === additionalContract?.desutdRegErrorCode
	)
	const allowRejectEmployer = isEmployer && additionalContractState === '7'
	const allowRejectEmployee = isEmployee && additionalContractState === '4'
	const isTeenager16 = adContractInfo?.under16 && Number.isFinite(adContractInfo?.age) && adContractInfo?.age < 16
	const isErrorTeenager16 = adContractInfo?.under16 && Number.isFinite(adContractInfo?.age) && adContractInfo?.age > 16
	const isVisibleTeenager = isEmployee && isTeenager16 && additionalContractState === '4'
	const isTeenDisabled = additionalContractState !== '4' || (adContractInfo?.under16 && statusKdp?.code !== '3')
	const contractType = 'eAdContract'

	useEffect(() => {
		const result = !Boolean(positionsByKnz.find(({ code }) => code === dpositionCode))
		setDisableButtonNkz(result)
	}, [positionsByKnz, additionalContract])

	useEffect(async () => {
		if (econtractFound) {
			setMode('loading')
			const res = await request({
				url: '/contract/eAdContract/get',
				method: 'GET',
				params: { externalId: additionalContract.externalId },
			})
			if (res.file)
				setEAdContractToSend({
					additionalContractDTO: additionalContract,
					externalId: additionalContract.externalId,
					fileId: res.file.id,
					optionsList: [],
					valueList: [],
					downloadFile: res.downloadFile,
					file: res.file,
				})
			setAdContractInfo(res)
		}
	}, [])

	useEffect(() => {
		if (adContractInfo) {
			setMode(null)
		}
	}, [adContractInfo])

	useEffect(() => {
		if (isVisibleTeenager) {
			updateStatusKdp()
		}
	}, [isVisibleTeenager])

	const onDelete = () => {
		onToggleAction('removeAdditionalAgreement', true)
		onToggleAction('viewAdditionalAgreement', false)
	}

	const onEdit = () => {
		onToggleAction('editAdditionalAgreement', true)
		onToggleAction('viewAdditionalAgreement', false)
	}

	const onSignAdditionalAgreement = () => {
		if (isTeenager16 && isEmployee && !adContractInfo.consentDate) {
			onToggleAction('signAdditionalAgreementTeenager', true)
		} else {
			onToggleAction('signAdditionalAgreement', true)
		}
		onToggleAction('viewAdditionalAgreement', false)
	}

	const onDecline = () => {
		onToggleAction('declineAdditionalAgreement', true)
		onToggleAction('viewAdditionalAgreement', false)
	}

	const downloadContractPDF = async (e) => {
		e.preventDefault()
		e.stopPropagation()
		setDisableDownload(true)
		setTimeout(() => {
			setDisableDownload(false)
		}, 5000)
		const response = await downloadFileByUid(adContractInfo.downloadFile.id)
		const file = URL.createObjectURL(response)
		const a = document.createElement('a')
		a.href = file
		a.download = adContractInfo.downloadFile.fileName
		document.body.appendChild(a)
		a.click()
	}

	const handleSendToEmployee = async (e) => {
		e.preventDefault()
		e.stopPropagation()
		if (additionalContract?.externalId)
			try {
				setMode('loading')
				await request({
					url: '/contract/eAdContract/send',
					method: 'POST',
					params: {
						externalId: additionalContract?.externalId,
					},
				})
				dispatch(contractsThunks.getContract(externalId, isLocalContract))
				dispatch(contractsActions.setAdditionalContractSubmitted())
				dispatch(contractsThunks.getContracts(filters, page, pageSize))
				message.success(t('success_alert'), 5)
			} catch (error) {
				const err = error?.response?.data?.message || t('unknown_error')
				message.error(err ? err : t('unknown_error'), 5)
			} finally {
				setMode(null)
			}
	}

	const CloseAndUpdateFilter = () => {
		dispatch(contractsActions.resetUpdateIinXml())
		dispatch(contractsThunks.getContract(externalId, isLocalContract))
		setFilters({ ...filters, dcontractStatusCode: '6' })
		fetchData({ ...filters, dcontractStatusCode: '6' })
		onClose()
	}

	const handleCheckboxChange = (value) => {
		setIsGuardian(value)
	}

	const getRepresentatives = () => {
		let body = {
			data: { externalId: additionalContract.externalId, guardian: isGuardian },
			type: contractType,
			onSuccess: (res) => {
				setStatusKdp(res)
				message.success(t('success_get_representatives'))
			},
		}
		dispatch(contractsThunks.sendKdpFamily(body))
	}

	const updateStatusKdp = async () => {
		if (additionalContractState === '4' && adContractInfo?.under16 && statusKdp?.code !== '3') {
			const statusKdpResult = await contractsThunks.getStatusKdpFamily(additionalContract.externalId, contractType)
			if (statusKdpResult) {
				setStatusKdp(statusKdpResult)
				message.success(t('info_status_request'), 10)
			}
		}
	}

	return (
		<>
			<Modal windowClassName={styles.modal}>
				<ModalBackButton onClick={onClose} />
				<div className={`${styles.wrapper} scroll`}>
					<div className={styles.header}>
						<Title>
							{t('view_add_contr_title', {
								additionalContractNumber: additionalContract?.contractNum || ' ',
								contractNumber: contractNumber || ' ',
								registerDate: registerDate || ' ',
							})}
						</Title>
						<div className={styles.headerRight}>
							<SignStatus item={additionalContract?.dcontractState} />
						</div>
					</div>
					<p className={styles.identifier}>ID: {additionalContract?.externalId}</p>
					<div className={styles.subTitle}>
						{!additionalContract?.desutdRegErrorCode && (
							<>
								{t('esutd_reg_date')}: {additionalContract?.entryDate}
							</>
						)}
						{additionalContract?.desutdRegErrorCode && (
							<p className={styles.registrationDate}>
								{t('esutd_reg_date')}:{' '}
								<span className={styles.error}>
									{t('package_loading.contract_error_text', {
										errorText: errorData?.[language],
										errorNumber: errorData?.code,
									})}
								</span>
							</p>
						)}
					</div>
					{!econtractFound && (
						<div className={styles.actions}>
							{activeRole === 'ROLE_EMPLOYER' && state === '1' && additionalContractState !== '1' && status !== '2' && (
								<Button textColor="green" buttonClassName={styles.button} onClick={onSign} disabled={disableButtonNkz}>
									<Pencil className={styles.iconWhite} />
									{t('sign_button')}
								</Button>
							)}
							{activeRole === 'ROLE_EMPLOYER' && status !== '2' ? (
								<>
									<Button borderColor="green" transparent border buttonClassName={styles.button} onClick={onEdit}>
										<Edit className={styles.iconGreen} />
										{t('edit_button')}
									</Button>
									<Button type="bare" buttonClassName={styles.button} onClick={onDelete}>
										<Trash className={styles.iconGrey} />
										{t('remove')}
									</Button>
								</>
							) : null}
						</div>
					)}
					{!fromFile && econtractFound && ['1', '2'].includes(status) && additionalContractState === '1' ? (
						<div className={styles.actions}>
							<Button onClick={(e) => downloadContractPDF(e)} disabled={disableDownload}>
								{t('download_pdf_eds')}
							</Button>
						</div>
					) : null}
					{econtractFound && fromFile && (
						<div className={styles.actions}>
							<Button onClick={() => setShowFile(true)} disabled={disableDownload}>
								{t('open_eds')}
							</Button>
						</div>
					)}
					<AdditionalContractInformation additionalContract={additionalContract} positionsByKnz={positionsByKnz} />
					{additionalContract?.files?.length > 0 ? (
						<div className={styles.mt44}>
							<Title>{t('labor_records.confirming_document')}</Title>
							<ViewConfirmDocument data={additionalContract?.files} additionalContract={true}></ViewConfirmDocument>
						</div>
					) : null}
					{/*Статус ЭДС*/}
					{econtractFound ? (
						<div className={styles.etd__wrapper}>
							<div className={styles.etd__titile}>{t('edit_confirm_contract')}</div>
							<div dangerouslySetInnerHTML={{ __html: adContractInfo?.textHtmlAd }} style={{ marginBottom: 32 }} />
							{adContractInfo?.note && (
								<div className={styles.employee__comment__wrapper}>
									<div className={styles.etd__title}>{t('employee_comment')}</div>
									<div className={styles.employee__comment}>{adContractInfo.note}</div>
								</div>
							)}
							{/*Несовершеннолетние начало*/}
							{isVisibleTeenager ? (
								<div className={'mt-24'}>
									<Alert
										icon={<Icon component={InfoGreen} className={styles.icon__alert} />}
										text={
											<p className={styles.text}>
												<Trans i18nKey={'fl16_alert'} components={{ italic: <i /> }} />
											</p>
										}
									/>
									{additionalContractState === '4' && statusKdp?.code !== '3' ? (
										<div className={'mt-16'}>
											<Checkbox
												name="flRepresentative_Checkbox"
												label={t('isRepresentative')}
												onChange={handleCheckboxChange}
												value={isGuardian}
											/>
										</div>
									) : null}
									<div>
										<FormButton
											buttonClassName={'mt-16'}
											type="button"
											onClick={getRepresentatives}
											disabled={
												!['0', '6', 'INVALID', 'PENDING', 'TIMEOUT', 'NOT_FOUND', 'ERROR', 'KDP_ERROR'].includes(
													statusKdp?.code
												)
											}
										>
											{t('fl16_button')}
										</FormButton>
										{statusKdp?.code !== '0' ? (
											<div className={styles.refreshWrapper}>
												<div className={'mr-16'}>
													<strong>{t('personal_affairs.status')}</strong>
													{language === 'rus' ? statusKdp.nameRu : statusKdp.nameKz}
												</div>
												{statusKdp?.code !== '3' && (
													<div className={styles.updateButton} onClick={updateStatusKdp}>
														<RefreshIcon className={styles.refreshIcon} />
														<span>{t('staff.refreshStatus')}</span>
													</div>
												)}
											</div>
										) : (
											<div className={'mb-32'}></div>
										)}
									</div>
								</div>
							) : null}
							{isErrorTeenager16 ? (
								<div className={'mt-24'}>
									<Alert
										icon={<InfoMedium className={styles.icon__alertRed} />}
										text={
											<p className={styles.text}>
												<Trans i18nKey={'errorTeenager16'} components={{ italic: <i /> }} />
											</p>
										}
									/>
								</div>
							) : null}
							{/*Несовершеннолетние конец*/}
							{adContractInfo && additionalContractState !== '3' ? (
								<TableSign currentEtd={adContractInfo} title={t('electronic_contract.status_sign_eds')} />
							) : null}
						</div>
					) : null}
					{econtractFound &&
					activeRole === 'ROLE_EMPLOYER' &&
					['3', '5', '6', '8', '9'].includes(additionalContractState) &&
					status === '1' ? (
						<div className={styles.buttons__wrapper}>
							<ButtonMui
								type="button"
								disabled={additionalContractState === '4'}
								variant="text"
								classes={{ root: cn(styles.button_etd, styles.reset__button), disabled: styles.button__disabled }}
								startIcon={<Delete fill={'#5B6880'} className={styles.edit__icon} />}
								onClick={onDelete}
							>
								{t('remove')}
							</ButtonMui>
							<Button type="submit" onClick={handleSendToEmployee} buttonClassName={styles.send__worker}>
								{t('send_to_sign_worker')}
							</Button>
						</div>
					) : null}
					{/*Кнопка отозвать*/}
					{econtractFound && activeRole === 'ROLE_EMPLOYER' && additionalContractState === '4' ? (
						<div className={styles.decline__button}>
							<Button type="submit" onClick={onRevert}>
								{t('actions.revoke')}
							</Button>
						</div>
					) : null}
					<div className={styles.footer}>
						{econtractFound &&
						adContractInfo &&
						additionalContractState === '4' &&
						activeRole === 'ROLE_EMPLOYEE' &&
						adContractInfo?.signEmployeeDate == null ? (
							<Button
								buttonClassName={`ms-3 ${styles.button}`}
								onClick={onRevision}
								disabled={disableButtonNkz || isTeenDisabled}
							>
								{t('send_to_rework')}
							</Button>
						) : null}
						{allowRejectEmployer && (
							<Button buttonClassName={`ms-3 ${styles.button}`} onClick={onDecline}>
								{t('decline')}
							</Button>
						)}
						{allowRejectEmployee && (
							<Button buttonClassName={`ms-3 ${styles.button}`} onClick={onDecline} disabled={isTeenDisabled}>
								{t('decline')}
							</Button>
						)}
						{econtractFound && adContractInfo && (status === '1' || status === '3') ? (
							<>
								{!['3', '4', '5', '6', '8'].includes(additionalContractState) &&
									activeRole === 'ROLE_EMPLOYER' &&
									adContractInfo?.signEmployerDate == null && (
										<Button
											buttonClassName={`ms-3 ${styles.button}`}
											onClick={onSignAdditionalAgreement}
											disabled={disableButtonNkz}
										>
											{t('sign_button')}
										</Button>
									)}
								{!['3', '6', '8'].includes(additionalContractState) &&
									activeRole === 'ROLE_EMPLOYEE' &&
									adContractInfo?.signEmployeeDate == null && (
										<Button
											buttonClassName={`ms-3 ${styles.button}`}
											onClick={onSignAdditionalAgreement}
											disabled={disableButtonNkz || isTeenDisabled}
										>
											{t('sign_button')}
										</Button>
									)}
							</>
						) : null}
					</div>
				</div>
			</Modal>
			{showFile && eAdContractToSend && (
				<EmploymentContractFile
					onClose={() => setShowFile(false)}
					documentData={adContractInfo.file}
					attachProjectFileData={eAdContractToSend}
					CloseAndUpdateFilter={CloseAndUpdateFilter}
					filters={filters}
					page={page}
					pageSize={pageSize}
					saveUrl={'/contract/eAdContract/save'}
					title={t('e_ad_contract')}
					changeFile={true}
					downloadFile={true}
				/>
			)}
			{mode === 'loading' ? <LoadingBlocker /> : null}
		</>
	)
}
